<template>
  <div class="analyze-idle">
    <div class="container">

      <!-- 門檻 tabs -->
      <div class="threshold-tabs">
        <button
          v-for="t in thresholds"
          :key="t.value"
          class="threshold-tab"
          :class="{ active: t.value === selectedThreshold }"
          @click="selectedThreshold = t.value"
        >
          {{ t.label }}
          <span class="count fs-12px">{{ counts[t.value] }}</span>
        </button>
      </div>

      <!-- Header summary -->
      <section class="hero-section" v-if="filteredCloths.length > 0">
        <D3PercentageDonutChart
          :key="selectedThreshold"
          :percentage="idlePercentage"
          :size="180"
          :thickness="20"
          color="#B5651D"
        />
        <div class="hero-label fs-14px">
          {{ filteredCloths.length }} {{ summaryLabel }}
        </div>
        <p class="hero-msg fs-14px">{{ heroMsg }}</p>
      </section>

      <!-- 單品 list -->
      <section class="cloth-grid" v-if="filteredCloths.length > 0">
        <div v-for="c in filteredCloths" :key="c.serial" class="cloth-card">
          <div class="cloth-thumb" :style="thumbStyle(c)"></div>
          <div class="cloth-info">
            <div class="cloth-name fs-14px fw-bold">{{ c.name || '未命名單品' }}</div>
            <div class="cloth-meta fs-12px">{{ formatLastWorn(c) }}</div>
            <div class="cloth-extra fs-12px" v-if="c.price > 0">原價 ${{ c.price }}</div>
          </div>
        </div>
      </section>

      <!-- Empty -->
      <section class="empty-state" v-else>
        <p class="fs-14px">{{ emptyMsg }}</p>
      </section>

    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import D3PercentageDonutChart from '@/views/test/D3PercentageDonutChart.vue';

const DAYS = {
  THREE_MONTHS: 90,
  SIX_MONTHS: 180,
  ONE_YEAR: 365,
};

export default {
  name: 'AnalyzeIdleItems',
  components: {
    D3PercentageDonutChart,
  },
  data() {
    return {
      selectedThreshold: 'six',
      thresholds: [
        { value: 'three', label: '3 個月以上' },
        { value: 'six', label: '6 個月以上' },
        { value: 'year', label: '1 年以上' },
        { value: 'never', label: '從未穿過' },
      ],
    };
  },
  computed: {
    ...mapState(['outfits', 'cloths']),

    activeCloths() {
      return (this.cloths || []).filter(c =>
        !c.isDeleted && c.recycleType === -1
      );
    },

    // 對每件單品計算最後穿著時間
    clothLastWornMap() {
      const map = new Map();
      for (const o of (this.outfits || [])) {
        if (!o.available || !o.date) continue;
        for (const p of (o.outfitPhotos || [])) {
          if (p.type === 'cloth' && p.clothSerial !== undefined) {
            const existing = map.get(p.clothSerial);
            if (!existing || o.date > existing) {
              map.set(p.clothSerial, o.date);
            }
          }
        }
      }
      return map;
    },

    // 為每件 active cloth 補上 lastWornDate / daysSince / neverWorn
    clothsWithUsage() {
      const today = new Date();
      return this.activeCloths.map(c => {
        const lastWorn = this.clothLastWornMap.get(c.serial);
        if (!lastWorn) {
          return { ...c, lastWornDate: null, daysSince: null, neverWorn: true };
        }
        const lastDate = new Date(lastWorn);
        const days = Math.floor((today - lastDate) / (1000 * 60 * 60 * 24));
        return { ...c, lastWornDate: lastWorn, daysSince: days, neverWorn: false };
      });
    },

    counts() {
      const c = this.clothsWithUsage;
      return {
        three: c.filter(cl => !cl.neverWorn && cl.daysSince >= DAYS.THREE_MONTHS).length,
        six: c.filter(cl => !cl.neverWorn && cl.daysSince >= DAYS.SIX_MONTHS).length,
        year: c.filter(cl => !cl.neverWorn && cl.daysSince >= DAYS.ONE_YEAR).length,
        never: c.filter(cl => cl.neverWorn).length,
      };
    },

    filteredCloths() {
      const c = this.clothsWithUsage;
      let filtered;
      if (this.selectedThreshold === 'never') {
        filtered = c.filter(cl => cl.neverWorn);
      } else {
        const threshold = this.selectedThreshold === 'three' ? DAYS.THREE_MONTHS
          : this.selectedThreshold === 'six' ? DAYS.SIX_MONTHS
          : DAYS.ONE_YEAR;
        filtered = c.filter(cl => !cl.neverWorn && cl.daysSince >= threshold);
      }
      // 排序：最久沒穿的在前
      return filtered.sort((a, b) => {
        if (a.neverWorn && b.neverWorn) return 0;
        if (a.neverWorn) return -1;
        if (b.neverWorn) return 1;
        return b.daysSince - a.daysSince;
      });
    },

    summaryLabel() {
      const t = this.thresholds.find(t => t.value === this.selectedThreshold);
      if (this.selectedThreshold === 'never') return '件單品從未被穿過';
      return `件單品超過 ${t.label.replace(' 以上', '')}沒穿`;
    },
    idlePercentage() {
      const total = this.activeCloths.length;
      if (total === 0) return 0;
      return Math.round(this.filteredCloths.length / total * 100);
    },

    heroMsg() {
      const total = this.activeCloths.length;
      if (total === 0) return '';
      const pct = Math.round(this.filteredCloths.length / total * 100);
      if (pct >= 50) return `佔了使用中單品的 ${pct}% — 是時候整理一下了`;
      if (pct >= 30) return `佔了使用中單品的 ${pct}% — 考慮看看哪些可以轉賣或捐贈`;
      return `佔了使用中單品的 ${pct}% — 不多，但還是試著穿穿看吧`;
    },

    emptyMsg() {
      if (this.selectedThreshold === 'never') return '太棒了！每一件單品都被穿過';
      const t = this.thresholds.find(t => t.value === this.selectedThreshold);
      return `沒有單品超過 ${t.label.replace(' 以上', '')}沒穿 — 你的衣櫥很活躍！`;
    },
  },
  methods: {
    thumbStyle(cloth) {
      const url = (cloth.imgs && cloth.imgs[0]) || '';
      return url ? { backgroundImage: `url(${url})` } : { backgroundColor: '#F4F4F4' };
    },
    formatLastWorn(c) {
      if (c.neverWorn) return '從未穿過';
      const d = c.daysSince;
      if (d < 30) return `${d} 天沒穿了`;
      if (d < 365) {
        const months = Math.floor(d / 30);
        return `${months} 個月沒穿了`;
      }
      const years = (d / 365).toFixed(1);
      return `${years} 年沒穿了`;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/basic";

.analyze-idle {
  padding: .5rem 0 2rem;
}

// ===== 門檻切換 =====
.threshold-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: .5rem;
  margin-bottom: 1.25rem;
}
.threshold-tab {
  display: flex;
  align-items: center;
  gap: .375rem;
  padding: .4rem 1rem;
  background-color: #fff;
  border: 1px solid $color-border;
  border-radius: 999px;
  color: $typography-secondary-default;
  font-size: 14px;
  cursor: pointer;
  transition: all .15s ease;
  &:hover {
    background-color: rgba(0, 0, 0, .03);
  }
  &.active {
    background-color: $color-primary;
    border-color: $color-primary;
    color: #fff;
    font-weight: bold;
    .count {
      background-color: rgba(255, 255, 255, .25);
      color: #fff;
    }
  }
  .count {
    background-color: #F4F4F4;
    color: $typography-secondary-default;
    padding: 1px 8px;
    border-radius: 999px;
    font-weight: bold;
  }
}

// ===== Hero =====
.hero-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: .5rem;
  padding: 1.5rem 0 2rem;
  text-align: center;
}
.hero-label {
  color: $typography-primary-default;
  margin: .25rem 0 0;
}
.hero-msg {
  color: $typography-secondary-default;
  margin: 0;
}

// ===== 單品 grid =====
.cloth-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: .75rem;
  @media (min-width: 576px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @include bigger-than-large {
    grid-template-columns: repeat(4, 1fr);
  }
}
.cloth-card {
  background-color: #fff;
  border: 1px solid $color-border;
  border-radius: .75rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.cloth-thumb {
  width: 100%;
  padding-top: 100%;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  background-color: #F4F4F4;
  border-bottom: 1px solid $color-border;
}
.cloth-info {
  padding: .75rem;
  .cloth-name {
    color: $typography-primary-default;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-bottom: .25rem;
  }
  .cloth-meta {
    color: $color-warning;
  }
  .cloth-extra {
    color: $typography-secondary-default;
    margin-top: 2px;
  }
}

// ===== 空狀態 =====
.empty-state {
  background-color: #fff;
  border: 1px solid $color-border;
  border-radius: .75rem;
  padding: 3rem 1.25rem;
  text-align: center;
  color: $typography-secondary-default;
  p { margin: 0; }
}
</style>
